import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '../context/CurrencyContext';
import './LegacyCollection.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHEMA_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Binding Thread Legacy Collection",
  "description": "A keepsake letter service for families facing terminal illness. We give your loved one a free number to call any time. They share a memory. We craft it into a beautiful letter and deliver it to your door.",
  "provider": {
    "@type": "Organization",
    "name": "Binding Thread",
    "url": "https://www.bindingthread.com.au"
  },
  "areaServed": "AU",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Legacy Collections",
    "itemListElement": [
      { "@type": "Offer", "name": "A Thread", "description": "Three beautifully crafted keepsake letters. Keepsake tin and shipping included.", "price": "149", "priceCurrency": "AUD" },
      { "@type": "Offer", "name": "Woven", "description": "Six beautifully crafted keepsake letters. Keepsake tin and shipping included.", "price": "249", "priceCurrency": "AUD" },
      { "@type": "Offer", "name": "Tapestry", "description": "Twelve beautifully crafted keepsake letters. Keepsake tin and shipping included.", "price": "429", "priceCurrency": "AUD" }
    ]
  }
};

const TIERS = [
  {
    id: 'legacy-thread',
    name: 'A Thread',
    aud: 149,
    usd: 99,
    desc: "Three beautifully crafted keepsake letters capturing the memories that matter most. A meaningful gift collection for the stories that deserve to be preserved first.",
    detail: '3 keepsake letters · Keepsake tin & shipping included',
    cta: 'Begin with A Thread',
  },
  {
    id: 'legacy-woven',
    name: 'Woven',
    aud: 249,
    usd: 169,
    desc: "Six letters, each one a piece of a life — a memory preservation collection that builds into something your family will treasure for generations.",
    detail: '6 keepsake letters · Keepsake tin & shipping included',
    cta: 'Begin Woven',
  },
  {
    id: 'legacy-tapestry',
    name: 'Tapestry',
    aud: 429,
    usd: 289,
    desc: "Twelve letters. Twelve memories. A complete legacy collection for the family who will one day want to know exactly who their loved one really was.",
    detail: '12 keepsake letters · Keepsake tin & shipping included',
    cta: 'Begin Tapestry',
  },
];

export default function LegacyCollection() {
  const { currency } = useCurrency();
  const collectionsRef = useRef(null);
  const [loadingTier, setLoadingTier] = useState(null);

  // SEO + JSON-LD schema injection
  useEffect(() => {
    document.title = 'Legacy Letters for Families Facing Terminal Illness | Binding Thread';

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    const description = 'Binding Thread captures memories and stories for families facing terminal illness. A free number to call any time, beautiful keepsake letters delivered to your door. A legacy your family will treasure forever. Collections from $149.';

    ensureMeta('meta[name="description"]', { name: 'description', content: description });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: 'Legacy Letters for Families Facing Terminal Illness | Binding Thread' });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: 'https://www.bindingthread.com.au/legacy-collection' });

    // canonical
    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://www.bindingthread.com.au/legacy-collection';

    // JSON-LD
    let ld = document.head.querySelector('script[data-lc-schema="legacy"]');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-lc-schema', 'legacy');
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(SCHEMA_JSONLD);

    return () => {
      const stale = document.head.querySelector('script[data-lc-schema="legacy"]');
      if (stale) stale.remove();
    };
  }, []);

  // Nav background opacity on scroll
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToCollections = (e) => {
    if (e) e.preventDefault();
    if (collectionsRef.current) {
      collectionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBeginTier = async (tierId) => {
    if (loadingTier) return;
    setLoadingTier(tierId);
    try {
      const checkoutCurrency = (currency || 'AUD').toLowerCase();
      const response = await axios.post(`${API_URL}/api/checkout/create-session`, {
        product_id: tierId,
        origin_url: window.location.origin,
        add_keepsake_tin: false,
        currency: checkoutCurrency,
        shipping_option: null,
      });
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setLoadingTier(null);
      }
    } catch (error) {
      console.error('Legacy checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoadingTier(null);
    }
  };

  const formatTierPrice = (tier) => {
    if (currency === 'USD') return { amount: `$${tier.usd}`, code: 'USD' };
    return { amount: `$${tier.aud}`, code: 'AUD' };
  };

  return (
    <div className="lc-page" data-testid="legacy-collection-page">
      {/* NAVIGATION */}
      <nav
        className="lc-nav"
        role="navigation"
        aria-label="Main navigation"
        style={scrolled ? { background: 'rgba(245,240,232,0.98)' } : undefined}
        data-testid="legacy-nav"
      >
        <Link to="/" aria-label="Binding Thread home" data-testid="legacy-nav-home-link">
          <img
            className="lc-nav-logo"
            src="/logo-final.png"
            alt="Binding Thread"
            width="240"
            height="64"
          />
        </Link>
        <button
          type="button"
          className="lc-nav-cta"
          onClick={scrollToCollections}
          data-testid="legacy-nav-cta-see-collections"
        >
          See Collections
        </button>
      </nav>

      {/* HERO */}
      <section className="lc-hero" aria-labelledby="lc-hero-heading">
        <svg
          className="lc-deco-thread"
          style={{ top: 0, right: 0, width: '45%', height: '70%', opacity: 0.06 }}
          viewBox="0 0 400 600"
          fill="none"
          aria-hidden="true"
        >
          <path d="M400 0 Q280 120 220 280 Q160 440 100 600" stroke="#c9a96e" strokeWidth="2" fill="none" />
          <path d="M400 80 Q290 190 240 340 Q190 490 140 600" stroke="#c9a96e" strokeWidth="1" fill="none" />
        </svg>
        <svg
          className="lc-deco-thread"
          style={{ bottom: 0, left: 0, width: '30%', height: '40%', opacity: 0.05 }}
          viewBox="0 0 240 320"
          fill="none"
          aria-hidden="true"
        >
          <path d="M0 320 Q60 200 140 100 Q200 30 240 0" stroke="#7a8c6e" strokeWidth="2" fill="none" />
        </svg>

        <div className="lc-hero-grid">
          <div className="lc-hero-copy">
            <span className="lc-tag">Legacy Collection</span>
            <h1 className="lc-hero-headline" id="lc-hero-heading" data-testid="legacy-hero-headline">
              Their stories will disappear unless someone captures them. That&apos;s why we exist.
            </h1>
            <p className="lc-hero-sub">
              <strong>Binding Thread</strong> is a keepsake letter service for families facing terminal illness. We give your loved one a free number to call any time — day or night. They share a memory. We craft it into a beautiful letter and deliver it to your door. <strong>Collections from $99.</strong>
            </p>
            <button
              type="button"
              className="lc-btn-primary"
              onClick={scrollToCollections}
              data-testid="legacy-hero-cta-see-collections"
            >
              See the Collections
            </button>
          </div>
          <div className="lc-hero-visual" aria-hidden="true">
            <div className="lc-keepsake-photo-frame">
              <img
                src="/legacy/keepsake-letter.jpg"
                alt=""
                className="lc-keepsake-photo"
                loading="eager"
                width="600"
                height="780"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="lc-who" aria-labelledby="lc-who-heading">
        <div className="lc-wide">
          <span className="lc-tag">Who this is for</span>
          <div className="lc-gline" style={{ margin: '12px 0 0' }}></div>
          <h2 className="lc-who-headline" id="lc-who-heading">
            You already know why you&apos;re here.
          </h2>
          <div className="lc-who-grid">
            <div className="lc-who-copy">
              <p className="lc-who-body">
                Whether you&apos;re the one lying awake wondering what to leave behind for the people you love, a partner searching for a way to preserve the memories of someone you&apos;re losing, or a palliative care professional looking for something meaningful to offer patients and their families — this page is for you.
              </p>
              <p className="lc-who-body" style={{ marginTop: 20 }}>
                Families facing terminal illness find themselves asking the same questions. How do I be remembered by the people I love — on my own terms, in my own words? That&apos;s what Binding Thread is built for.
              </p>
              <p className="lc-who-body" style={{ marginTop: 20 }}>
                These are the questions Binding Thread answers. Memory preservation during terminal illness, handled with complete care — so you don&apos;t have to.
              </p>
            </div>
            <aside className="lc-who-questions" role="note" aria-label="Common questions families ask">
              <p>How do I record my memories for my kids before I die?</p>
              <p>What can I leave behind that will last?</p>
              <p>What to leave behind for the people I love when I&apos;m gone?</p>
              <p>How do I make sure my family knows who I really was?</p>
            </aside>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lc-how" aria-labelledby="lc-how-heading">
        <div className="lc-wide">
          <div className="lc-section-head-center">
            <span className="lc-tag">How it works</span>
            <div className="lc-gline lc-gline-center" style={{ margin: '12px auto 0' }}></div>
            <h2 className="lc-how-headline" id="lc-how-heading">Three steps. Nothing complicated.</h2>
            <p className="lc-how-intro">Nothing that requires energy you don&apos;t have.</p>
          </div>

          <div className="lc-steps-grid" role="list">
            <div className="lc-step-card" role="listitem">
              <div className="lc-step-num" aria-hidden="true">01</div>
              <h3 className="lc-step-title">Choose a collection</h3>
              <p className="lc-step-body">
                Whether you&apos;re buying this as a gift for someone with a terminal diagnosis, signing up for a loved one in palliative care, or doing this for yourself — it takes less than five minutes. You tell us who the letters are for. We handle everything else.
              </p>
            </div>

            <div className="lc-step-card" role="listitem">
              <div className="lc-step-num" aria-hidden="true">02</div>
              <h3 className="lc-step-title">They call a free number whenever they feel ready</h3>
              <p className="lc-step-body">
                No apps. No technology to learn. No scheduling. Just a phone — from a hospital bed, a hospice room, or home. Day or night, whenever a memory surfaces, they call and record it in their own words.
              </p>
            </div>

            <div className="lc-step-card" role="listitem">
              <div className="lc-step-num" aria-hidden="true">03</div>
              <h3 className="lc-step-title">A keepsake letter arrives at your door</h3>
              <p className="lc-step-body">
                We transform each recording into a beautifully crafted letter — printed, sealed and posted. A piece of them, preserved in their own words. A last message, a story, a voice that stays.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KEEPSAKE BOX */}
      <section className="lc-keepsake" aria-labelledby="lc-box-heading">
        <div className="lc-wide">
          <div className="lc-keepsake-inner">
            <div>
              <span className="lc-tag" style={{ color: 'var(--lc-pink)' }}>The keepsake box</span>
              <div className="lc-gline" style={{ margin: '12px 0 0' }}></div>
              <h2 className="lc-keepsake-headline" id="lc-box-heading">
                It starts with something to hold.
              </h2>
              <p className="lc-keepsake-body">
                Your Binding Thread keepsake box arrives with your first letter — a home for everything that follows. Each letter is added to the collection, building into something the people you love will reach for again and again.
              </p>
              <p className="lc-keepsake-body" style={{ marginTop: 16 }}>
                A physical legacy. A terminal illness legacy gift that sits on a shelf, gets handed down, and never disappears. Something your family will open and feel the presence of someone they loved.
              </p>
            </div>
            <div className="lc-keepsake-visual" aria-hidden="true">
              <div className="lc-tin-photo-frame">
                <img
                  src="/legacy/keepsake-tin.png"
                  alt=""
                  className="lc-tin-photo"
                  loading="lazy"
                  width="800"
                  height="800"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section
        className="lc-collections"
        id="collections"
        ref={collectionsRef}
        aria-labelledby="lc-collections-heading"
      >
        <div className="lc-wide">
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <span className="lc-tag">The Legacy Collections</span>
          </div>
          <div className="lc-gline lc-gline-center" style={{ marginBottom: 0 }}></div>
          <h2 className="lc-collections-headline" id="lc-collections-heading">Choose your collection</h2>
          <p className="lc-collections-intro">
            Every collection includes the keepsake box, the free call number, and our care — from the very first call to the very last letter.
          </p>

          <div className="lc-tiers" role="list">
            {TIERS.map((tier) => {
              const { amount, code } = formatTierPrice(tier);
              return (
                <div className="lc-tier" role="listitem" key={tier.id} data-testid={`legacy-tier-${tier.id}`}>
                  <p className="lc-tier-name">{tier.name}</p>
                  <p className="lc-tier-price" data-testid={`legacy-tier-${tier.id}-price`}>
                    {amount} <span>{code}</span>
                  </p>
                  <div className="lc-tier-divider"></div>
                  <p className="lc-tier-desc">{tier.desc}</p>
                  <p className="lc-tier-detail">{tier.detail}</p>
                  <button
                    type="button"
                    className="lc-btn-tier"
                    onClick={() => handleBeginTier(tier.id)}
                    disabled={loadingTier !== null}
                    data-testid={`legacy-tier-${tier.id}-cta`}
                  >
                    {loadingTier === tier.id ? 'Loading…' : tier.cta}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="lc-tiers-soft-contact">
            Questions, or want to speak to someone before deciding?{' '}
            <a
              href="mailto:admin@bindingthread.com.au?subject=Legacy%20Collection%20enquiry"
              data-testid="legacy-soft-contact-link"
            >
              Talk to us
            </a>
            .
          </p>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="lc-reassure" aria-labelledby="lc-reassure-heading">
        <div className="lc-wide">
          <div className="lc-section-head-center">
            <span className="lc-tag">Everything considered</span>
            <div className="lc-gline lc-gline-center" style={{ margin: '12px auto 0' }}></div>
            <h2 className="lc-reassure-headline" id="lc-reassure-heading">
              We&apos;ve thought of everything so you don&apos;t have to.
            </h2>
          </div>
          <ul className="lc-reassure-list">
            <li>The keepsake box arrives with the first letter — everything together from day one</li>
            <li>The call number works on any phone — no smartphones, no apps, no learning curve</li>
            <li>Calls can be made from a hospital bed, a hospice room, or anywhere at all</li>
            <li>There&apos;s no schedule to keep — letters come when the calls do</li>
            <li>Every recording is handled with the discretion and care this moment deserves</li>
            <li>Gifts for palliative care patients can be arranged directly — contact us and we&apos;ll help</li>
          </ul>
        </div>
      </section>

      {/* CLOSING */}
      <section className="lc-close" aria-labelledby="lc-close-heading">
        <div className="lc-inner">
          <div className="lc-gline lc-gline-center" style={{ margin: '0 auto 24px' }}></div>
          <h2 className="lc-close-headline" id="lc-close-heading">
            When you&apos;re ready.
          </h2>
          <p className="lc-close-body">
            A legacy gift for families facing terminal illness, built around a single simple idea — that the people we love deserve to be remembered whole.
          </p>
          <button
            type="button"
            className="lc-btn-primary"
            onClick={scrollToCollections}
            data-testid="legacy-closing-cta-see-collections"
          >
            See the Collections
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lc-footer" role="contentinfo">
        <img
          className="lc-footer-logo"
          src="/logo-final.png"
          alt="Binding Thread"
          width="120"
          height="30"
        />
        <ul className="lc-footer-links" aria-label="Footer navigation">
          <li><Link to="/" data-testid="legacy-footer-home">Home</Link></li>
          <li><Link to="/how-it-works" data-testid="legacy-footer-how">How it works</Link></li>
          <li><Link to="/faq" data-testid="legacy-footer-contact">Contact</Link></li>
          <li><Link to="/privacy" data-testid="legacy-footer-privacy">Privacy</Link></li>
        </ul>
        <p className="lc-footer-copy">&copy; 2026 Binding Thread &nbsp;·&nbsp; bindingthread.com.au</p>
      </footer>
    </div>
  );
}
